import { LockClosedIcon } from "@heroicons/react/20/solid"
import { InformationCircleIcon } from "@heroicons/react/24/solid"
import { Link } from "react-router-dom"

import type {
  SolarConsumptionMonthBasis,
  SolarProposalConsumptionDto,
} from "~/api/dto/solar-proposal-consumption.dto"
import { Badge } from "~/components/ui-components/v2"
import type { BadgeTone } from "~/components/ui-components/v2"

import {
  GREEN_BUTTON_AVAILABLE_MESSAGE,
  GREEN_BUTTON_CONNECTED_MESSAGE,
  GREEN_BUTTON_NEWER_MESSAGE,
  GREEN_BUTTON_STEP_LINK_LABEL,
  MONTH_LABELS,
  consumptionSourceLine,
  formatCapturedDate,
  formatKwh,
  greenButtonBlockedMessage,
  monthBasisLabel,
} from "./solar-consumption-form"

const BASIS_TONES: Record<SolarConsumptionMonthBasis, BadgeTone> = {
  seller_entered: `blue`,
  vision_modeled: `gray`,
  provider_modeled: `gray`,
  measured: `green`,
  observed_modeled: `yellow`,
}

const GreenButtonNote = ({
  greenButton,
  greenButtonStepPath,
  selectedFromGreenButton,
}: {
  greenButton: SolarProposalConsumptionDto[`greenButton`]
  greenButtonStepPath: string | null
  selectedFromGreenButton: boolean
}): JSX.Element | null => {
  // A connection with nothing to switch to yet (still importing, or partial
  // data awaiting confirmation) still points to the step that handles it.
  const connectedOnly =
    !greenButton.available &&
    !greenButton.blockedReason &&
    greenButton.connectionStatus !== null &&
    !selectedFromGreenButton
  const message = greenButton.newerAvailable
    ? GREEN_BUTTON_NEWER_MESSAGE
    : greenButton.available
      ? GREEN_BUTTON_AVAILABLE_MESSAGE
      : greenButton.blockedReason
        ? greenButtonBlockedMessage(greenButton.blockedReason)
        : connectedOnly
          ? GREEN_BUTTON_CONNECTED_MESSAGE
          : null
  if (!message) return null
  const linked = greenButton.available || connectedOnly

  return (
    <p
      role="status"
      data-testid="solar-consumption-green-button"
      className="flex items-start gap-2 rounded-lg border border-border bg-surface-elevated p-3 text-sm text-foreground"
    >
      <InformationCircleIcon
        aria-hidden="true"
        className={`mt-0.5 size-4 shrink-0 ${greenButton.available ? `text-primary` : `text-muted`}`}
      />
      <span>
        {message}
        {linked && greenButtonStepPath ? (
          <>
            {` `}
            <Link
              to={greenButtonStepPath}
              className="font-semibold text-primary underline"
            >
              {GREEN_BUTTON_STEP_LINK_LABEL}
            </Link>
          </>
        ) : null}
      </span>
    </p>
  )
}

/**
 * The selected usage: the annual total, each month with where it came from,
 * the source, capture date and manual lock, and Green Button availability as
 * text, linking to the Green Button Data step that switches to it.
 */
export const SolarConsumptionCurrentUsage = ({
  consumption,
  greenButtonStepPath = null,
}: {
  consumption: SolarProposalConsumptionDto
  /** Where the Green Button Data step opens, linked from its note. */
  greenButtonStepPath?: string | null
}): JSX.Element => {
  const { selected, selection, greenButton } = consumption
  const captured = formatCapturedDate(selected?.capturedAt ?? null)

  return (
    <section
      aria-label="Current usage"
      data-testid="solar-consumption-current"
      className="space-y-4 rounded-xl border border-border bg-surface p-5"
    >
      <h3 className="text-xs font-semibold tracking-wide text-muted uppercase">
        Current usage
      </h3>
      {selected ? (
        <>
          <div className="space-y-1">
            <p className="font-display text-2xl font-semibold text-foreground tabular-nums">
              {selected.annualKwh == null
                ? `Annual total unknown`
                : `${formatKwh(selected.annualKwh)} / year`}
            </p>
            <p
              className="text-sm text-foreground-soft"
              data-testid="solar-consumption-source"
            >
              {consumptionSourceLine(selected)}
            </p>
            {captured ? (
              <p className="text-sm text-muted">Captured {captured}</p>
            ) : null}
            {selection?.lockedAt ? (
              <p
                className="flex items-center gap-1.5 text-sm text-muted"
                data-testid="solar-consumption-locked"
              >
                <LockClosedIcon aria-hidden="true" className="size-4" />
                Locked: manual entry
              </p>
            ) : null}
          </div>
          {selected.months.length === 12 ? (
            <ul
              aria-label="Monthly usage"
              className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2"
            >
              {selected.months.map(({ month, kwh, basis }) => (
                <li
                  key={month}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="w-8 text-muted">
                    {MONTH_LABELS[month - 1]}
                  </span>
                  <span className="flex-1 text-right text-foreground tabular-nums">
                    {formatKwh(kwh)}
                  </span>
                  <Badge
                    tone={BASIS_TONES[basis]}
                    className="w-20 justify-center"
                  >
                    {monthBasisLabel(basis)}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-muted">No usage has been entered yet.</p>
      )}

      <GreenButtonNote
        greenButton={greenButton}
        greenButtonStepPath={greenButtonStepPath}
        selectedFromGreenButton={selected?.source === `green_button`}
      />
    </section>
  )
}
